import sleep from "../../utils/sleep";
import { fetchResult } from "../../utils/fetch";
import { RelativeUrl } from "../../utils/environment";
import { difference } from "../../utils/set";
import { BatchServerResponse, Batch, isBatchComplete, isBatchPending } from "../../types/batch";
import { transformBatches } from "../../hooks/use-batches";
import Result from "../../types/result";
import { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

const POLL_INTERVAL_MILLIS = 2000;

export const BatchUrl = "batches";

/**
 * Query keys for batch-related queries
 */
export const batchQueryKeys = {
  batches: () => ["batches"] as const,
  initialBatches: (creatorId: number, themeId: number) => ["batches", "initial", creatorId, themeId] as const,
  batchesByIds: (ids: number[], creatorId?: number) => ["batches", "by-ids", ids, creatorId] as const
} as const;

/**
 * Determines if a batch has changed in a way that requires an update to the local store.
 * We avoid deep-equal on the entire info/result to keep it fast.
 */
export function batchPollSnapshotChanged(prev: Batch | undefined, next: Batch): boolean {
  if (!prev) return true;
  if (prev.status !== next.status) return true;
  if (prev.info?.phase !== next.info?.phase) return true;

  const prevProgress = prev.info?.agentic_progress;
  const nextProgress = next.info?.agentic_progress;

  if (!prevProgress && !nextProgress) return false;
  if (!prevProgress || !nextProgress) return true;

  // Compare specific fields of agentic_progress meaningfully
  return (
    prevProgress.current_loop !== nextProgress.current_loop ||
    prevProgress.max_loops !== nextProgress.max_loops ||
    prevProgress.phase !== nextProgress.phase ||
    prevProgress.action !== nextProgress.action ||
    prevProgress.updated_at !== nextProgress.updated_at
  );
}

export async function getInitialBatches(creatorId: number, themeId: number): Promise<Result<Batch[]>> {
  const p = new URLSearchParams();
  p.append("creator_id", creatorId.toString());
  p.append("theme_id", themeId.toString());
  p.append("is_completed", "false");
  const result = await fetchResult<BatchServerResponse[]>(`${RelativeUrl}/${BatchUrl}?${p.toString()}`);
  return result.map(transformBatches);
}

/**
 * TanStack Query hook for fetching initial batches
 */
export const useInitialBatches = (creatorId: number, themeId: number) => {
  return useQuery({
    queryKey: batchQueryKeys.initialBatches(creatorId, themeId),
    queryFn: async () => {
      const result = await getInitialBatches(creatorId, themeId);
      if (result.isError()) {
        throw new Error(result.error.message);
      }
      return result.value;
    },
    staleTime: 30 * 1000, // 30 seconds - batches change frequently during processing
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });
};

// we don't want to scope to current user in internal
async function getBatches(ids: number[], creatorId?: number): Promise<Result<Batch[]>> {
  const p = new URLSearchParams();
  if (creatorId) p.append("creator_id", creatorId.toString());
  ids?.forEach((id) => p.append("ids", id.toString()));
  const result = await fetchResult<BatchServerResponse[]>(`${RelativeUrl}/${BatchUrl}?${p.toString()}`);
  return result.map(transformBatches);
}

/**
 * TanStack Query hook for fetching batches by IDs with polling
 */
export const useBatchesByIds = (ids: number[], creatorId?: number, enabled = true) => {
  return useQuery({
    queryKey: batchQueryKeys.batchesByIds(ids, creatorId),
    queryFn: async () => {
      const result = await getBatches(ids, creatorId);
      if (result.isError()) {
        throw new Error(result.error.message);
      }
      return result.value;
    },
    enabled: enabled && ids.length > 0,
    staleTime: 0, // Always consider stale for real-time updates
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: POLL_INTERVAL_MILLIS, // Poll every 2 seconds
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });
};

/**
 * Modern TanStack Query-based batch polling hook
 * This replaces the old recursive polling logic with TanStack Query's built-in polling
 */
export const useBatchPollingWithTanStack = (
  batchesFromStore: Batch[],
  updateBatch: (batch: Batch) => void,
  creatorId?: number,
  onBatchComplete?: (batch: Batch) => void
) => {
  const [pendingIds, setPendingIds] = useState<number[]>([]);

  // Update pending IDs when batches change
  useEffect(() => {
    const newPendingIds = batchesFromStore.filter((batch) => isBatchPending(batch.status)).map((batch) => batch.id);

    const newPendingSet = new Set(newPendingIds);
    const currentPendingSet = new Set(pendingIds);

    if (difference(newPendingSet, currentPendingSet).size > 0) {
      setPendingIds(newPendingIds);
    }
  }, [batchesFromStore, pendingIds]);

  // Use TanStack Query for polling
  const { data: polledBatches } = useBatchesByIds(pendingIds, creatorId, pendingIds.length > 0);

  // Handle batch updates when polling returns data
  useEffect(() => {
    if (!polledBatches) return;

    polledBatches.forEach((batch) => {
      const existing = batchesFromStore.find((b) => b.id === batch.id);
      if (batchPollSnapshotChanged(existing, batch)) {
        updateBatch(batch);
      }

      if (isBatchComplete(batch.status)) {
        onBatchComplete?.(batch);
        // Remove from pending list since it's complete
        setPendingIds((prev) => prev.filter((id) => id !== batch.id));
      }
    });
  }, [polledBatches, updateBatch, onBatchComplete, batchesFromStore]);

  // Stop polling for completed batches by updating the pending list
  useEffect(() => {
    if (!polledBatches) return;

    const stillPending = pendingIds.filter((id) => {
      const batch = polledBatches.find((b) => b.id === id);
      // If batch is not found, keep polling (might be a temporary server issue)
      if (!batch) return true;
      return isBatchPending(batch.status);
    });

    if (stillPending.length !== pendingIds.length) {
      setPendingIds(stillPending);
    }
  }, [polledBatches, pendingIds]);
};

// Keep the original hook for backward compatibility
const useBatchPolling = (batchesFromStore: Batch[], updateBatch: (batch: Batch) => void, creatorId?: number) => {
  const [pending, setPending] = useState<number[]>([]);
  const batchesRef = useRef(batchesFromStore);

  useEffect(() => {
    batchesRef.current = batchesFromStore;
    const pendingIds = batchesFromStore.filter((batch) => isBatchPending(batch.status)).map((batch) => batch.id);
    const newPending = new Set(pendingIds);
    if (difference(newPending, new Set(pending)).size > 0) {
      setPending(pendingIds);
    }
  }, [batchesFromStore, pending]);

  useEffect(() => {
    let run = true;

    async function recursivePoll(ids: number[]) {
      const result = await getBatches(ids, creatorId);

      // if error, we would keep polling
      // which problably we don't want
      const batches = result.orElse([]);

      ids.forEach((id) => {
        const batch = batches.find((b) => b.id === id);
        if (batch) {
          const existing = batchesRef.current.find((b) => b.id === batch.id);
          if (batchPollSnapshotChanged(existing, batch)) {
            updateBatch(batch);
          }
        }
      });

      const pending = ids.filter((id) => {
        const batch = batches.find((b) => b.id === id);
        // if item is undefined, that means the response did not
        // contain any info about the id. problably keep polling
        if (!batch) return true;
        return isBatchPending(batch.status);
      });

      if (pending.length && run) {
        await sleep(POLL_INTERVAL_MILLIS);
        await recursivePoll(pending);
      }
    }

    if (pending.length) recursivePoll(pending);

    return () => {
      run = false;
    };
  }, [pending, creatorId, updateBatch]);
};

export default useBatchPolling;
